import { API_ORIGIN } from "@/lib/apiOrigin";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  TrendingUp, MousePointerClick, Building2, ArrowRight, Download,
  Calendar, Truck, Package, Clock, CheckCircle2, MapPin, RefreshCcw,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatCHF } from "@/lib/format";

const API_BASE = API_ORIGIN;

const WINDOWS = [
  { id: 7,   label: "7 giorni" },
  { id: 30,  label: "30 giorni" },
  { id: 90,  label: "90 giorni" },
  { id: 365, label: "1 anno" },
];

export default function AdminAcquisition() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/admin/acquisition/dashboard?days=${days}`);
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const maxDaily = useMemo(() => {
    if (!data?.daily) return 1;
    return Math.max(1, ...data.daily.map((d) => d.requested));
  }, [data]);

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("dapnow_access_token") || "";
      const res = await fetch(`${API_BASE}/api/admin/acquisition/export.csv?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error("Export non disponibile");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `dapnow_acquisition_${days}d.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || "Errore export");
    }
  };

  if (loading && !data) return <div className="card-soft p-10 text-center text-dapnow-muted">Caricamento…</div>;
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="admin-acquisition-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-dapnow-ink flex items-center gap-2">
            <TrendingUp className="size-6 text-dapnow-blue" />
            Acquisizione homepage
          </h1>
          <p className="text-sm text-dapnow-muted mt-1 max-w-2xl">
            Performance del calcolatore pubblico: preventivi richiesti, conversione a registrazione/lead business, top tratte e servizi.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-xl border border-zinc-200 bg-white p-1" data-testid="window-switcher">
            {WINDOWS.map((w) => (
              <button
                key={w.id}
                onClick={() => setDays(w.id)}
                data-testid={`window-${w.id}d`}
                className={`px-3 py-1 text-xs font-semibold rounded-lg transition ${days === w.id ? "bg-dapnow-ink text-white" : "text-dapnow-muted hover:text-dapnow-ink"}`}
              >
                {w.label}
              </button>
            ))}
          </div>
          <button
            onClick={load}
            data-testid="refresh-btn"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl border border-zinc-200 bg-white text-sm font-semibold hover:bg-zinc-50"
          >
            <RefreshCcw className={`size-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={exportCsv}
            data-testid="export-csv-btn"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-dapnow-blue text-white text-sm font-semibold hover:bg-dapnow-blue/90"
          >
            <Download className="size-4" /> CSV
          </button>
        </div>
      </header>

      {/* Funnel stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="funnel-stats">
        <StatCard
          testid="stat-quotes"
          icon={Package} tone="bg-dapnow-blue/10 text-dapnow-blue"
          label="Preventivi pubblici richiesti"
          value={data.totals.public_quote_requested}
        />
        <StatCard
          testid="stat-signup-clicks"
          icon={MousePointerClick} tone="bg-emerald-50 text-emerald-700"
          label="Click → Registrazione"
          value={data.totals.public_quote_converted_to_signup}
          subtitle={`${data.conversion_rate.quote_to_signup_click_pct}% conv`}
        />
        <StatCard
          testid="stat-business-clicks"
          icon={Building2} tone="bg-amber-50 text-amber-800"
          label="Click → Business"
          value={data.totals.public_business_lead_clicked}
          subtitle={`${data.conversion_rate.quote_to_business_lead_pct}% conv`}
        />
        <StatCard
          testid="stat-signups"
          icon={CheckCircle2} tone="bg-purple-50 text-purple-700"
          label="Registrazioni nel periodo"
          value={data.registrations.total_signups_in_window}
          subtitle={`${data.registrations.business_signups_in_window} business · ${data.registrations.b2b_leads_in_window} lead B2B`}
        />
      </div>

      {/* Daily sparkline */}
      {data.daily.length > 0 && (
        <section className="card-soft p-5" data-testid="daily-chart">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
              <Calendar className="size-4 text-dapnow-muted" /> Andamento giornaliero
            </h2>
            <div className="flex items-center gap-3 text-[11px] text-dapnow-muted">
              <span className="inline-flex items-center gap-1"><span className="size-2 rounded-full bg-dapnow-blue" /> Preventivi</span>
              <span className="inline-flex items-center gap-1"><span className="size-2 rounded-full bg-emerald-500" /> Registrazione</span>
              <span className="inline-flex items-center gap-1"><span className="size-2 rounded-full bg-amber-500" /> Business</span>
            </div>
          </div>
          <div className="flex items-end gap-1 h-32 overflow-x-auto">
            {data.daily.map((d) => {
              const h = (d.requested / maxDaily) * 100;
              const signupH = d.requested ? Math.min(100, (d.signup / Math.max(1, d.requested)) * h) : 0;
              const bizH = d.requested ? Math.min(100, (d.business / Math.max(1, d.requested)) * h) : 0;
              return (
                <div key={d.date} className="flex flex-col items-center gap-1 min-w-[18px] flex-1 max-w-[36px]" title={`${d.date}: ${d.requested} preventivi · ${d.signup} signup · ${d.business} business`}>
                  <div className="flex-1 w-full flex flex-col items-stretch justify-end">
                    <div className="w-full bg-dapnow-blue/20 rounded-t relative" style={{ height: `${h}%` }}>
                      <div className="absolute inset-x-0 bottom-0 bg-emerald-500 rounded-t" style={{ height: `${signupH}%` }} />
                      <div className="absolute inset-x-0 bottom-0 bg-amber-500/80" style={{ height: `${bizH}%` }} />
                    </div>
                  </div>
                  <span className="text-[9px] text-dapnow-muted">{d.date.slice(5)}</span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        <TopList
          testid="top-routes"
          title="Top tratte richieste"
          icon={MapPin}
          items={data.top_routes}
        />
        <TopList
          testid="top-services"
          title="Servizi più richiesti"
          icon={Truck}
          items={data.by_service}
        />
        <TopList
          testid="top-packages"
          title="Dimensioni pacco più richieste"
          icon={Package}
          items={data.by_package}
        />
        <TopList
          testid="top-timeslots"
          title="Fasce orarie"
          icon={Clock}
          items={data.by_time_slot}
        />
      </div>

      <section className="card-soft p-5" data-testid="price-stats">
        <h2 className="font-display text-lg font-bold tracking-tight">Prezzo stimato</h2>
        <div className="mt-3 grid grid-cols-3 gap-3">
          <PriceTile label="Medio" value={formatCHF(data.price.avg)} />
          <PriceTile label="Min" value={formatCHF(data.price.min)} />
          <PriceTile label="Max" value={formatCHF(data.price.max)} />
        </div>
        <p className="text-xs text-dapnow-muted mt-3">
          Calcolato su <strong>{data.price.count}</strong> preventivi nel periodo selezionato.
        </p>
      </section>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, subtitle, tone, testid }) {
  return (
    <div className="card-soft p-4" data-testid={testid}>
      <span className={`inline-flex size-8 items-center justify-center rounded-lg ${tone}`}>
        <Icon className="size-4" />
      </span>
      <p className="mt-3 text-2xl font-extrabold tabular-nums">{value}</p>
      <p className="text-[11px] uppercase tracking-wider font-semibold text-dapnow-muted mt-0.5">{label}</p>
      {subtitle && <p className="text-[11px] text-dapnow-muted mt-1">{subtitle}</p>}
    </div>
  );
}

function TopList({ title, items, icon: Icon, testid }) {
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <section className="card-soft p-5" data-testid={testid}>
      <h2 className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
        <Icon className="size-4 text-dapnow-muted" /> {title}
      </h2>
      {items.length === 0 ? (
        <p className="text-xs text-dapnow-muted mt-3">Nessun dato nel periodo.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {items.map((it) => (
            <li key={it.key} className="text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="truncate font-medium text-dapnow-ink">{it.key}</span>
                <span className="tabular-nums font-bold">{it.count}</span>
              </div>
              <div className="mt-1 h-1.5 bg-dapnow-line rounded-full overflow-hidden">
                <div className="h-full bg-dapnow-blue" style={{ width: `${(it.count / max) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function PriceTile({ label, value }) {
  return (
    <div className="rounded-xl bg-dapnow-bg px-3 py-3">
      <p className="text-[11px] uppercase font-bold tracking-wider text-dapnow-muted">{label}</p>
      <p className="text-lg font-extrabold font-display tabular-nums mt-0.5">{value}</p>
    </div>
  );
}
